import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Handles both /set-password (invite/activation) and /reset-password (forgot).
export default function SetPasswordPage() {
  const { validateResetToken, resetPassword, formatApiError } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [state, setState] = useState({ status: "checking", email: "", purpose: "reset", name: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Branding collected during account activation (invite flow)
  const [bizName, setBizName] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) { setState({ status: "invalid" }); return; }
      try {
        const { data } = await validateResetToken(token);
        if (active) {
          setState({ status: "valid", email: data.email, purpose: data.purpose, name: data.name || "" });
          setBizName(data.name || "");
          setContactEmail(data.email || "");
        }
      } catch (err) {
        if (active) setState({ status: "invalid", message: formatApiError(err) });
      }
    })();
    return () => { active = false; };
  }, [token, validateResetToken, formatApiError]);

  const isInvite = state.purpose === "invite";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (isInvite && (!bizName.trim() || !phone.trim() || !contactEmail.trim())) {
      setError("Please fill in your name/business, contact number and email — they appear on your reports.");
      return;
    }
    setLoading(true);
    try {
      const extra = isInvite
        ? { business_name: bizName.trim(), phone: phone.trim(), contact_email: contactEmail.trim() }
        : {};
      await resetPassword(token, password, extra);
      toast.success(isInvite ? "Account activated" : "Password updated");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (state.status === "checking") {
    return (
      <AuthShell title="Please wait" subtitle="Verifying your link…">
        <div className="flex items-center gap-3 text-slate-400 py-4" data-testid="token-checking">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="font-mono text-xs uppercase tracking-widest">Checking link…</span>
        </div>
      </AuthShell>
    );
  }

  if (state.status === "invalid") {
    return (
      <AuthShell title="Link not valid" subtitle="This link is invalid, expired, or already used.">
        <div data-testid="token-invalid" className="text-sm text-slate-300 leading-relaxed">
          {state.message || "Please ask your administrator for a new invitation, or request a new reset link."}
        </div>
        <div className="mt-6 pt-5 border-t border-slate-800 text-sm text-center flex flex-col gap-2">
          <Link to="/forgot-password" className="text-amber-400 hover:text-amber-300 font-semibold">Request a new reset link</Link>
          <Link to="/login" className="text-slate-400 hover:text-slate-200 inline-flex items-center justify-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={isInvite ? "Set your password" : "Choose a new password"}
      subtitle={isInvite
        ? `Activate your account for ${state.email}`
        : `Resetting the password for ${state.email}`}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="set-password-form">
        {isInvite && (
          <div className="space-y-4 pb-4 mb-1 border-b border-slate-800">
            <div className="text-xs text-slate-400 leading-relaxed">
              These details brand every page of the Vastu reports you export.
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bizname" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Name / Business</Label>
              <Input id="bizname" required data-testid="brand-name-input"
                value={bizName} onChange={(e) => setBizName(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100 h-11" placeholder="Your name or firm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Contact number</Label>
              <Input id="phone" required data-testid="brand-phone-input" type="tel"
                value={phone} onChange={(e) => setPhone(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100 h-11" placeholder="+91 98765 43210" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cemail" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Email</Label>
              <Input id="cemail" required data-testid="brand-email-input" type="email"
                value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100 h-11" placeholder="you@example.com" />
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="password" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">New password (min 6)</Label>
          <div className="relative">
            <Input
              id="password" type={showPw ? "text" : "password"} autoComplete="new-password" required minLength={6}
              data-testid="new-password-input"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="bg-slate-950 border-slate-800 text-slate-100 h-11 pr-10"
            />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 h-8 w-8 flex items-center justify-center">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Confirm password</Label>
          <Input
            id="confirm" type={showPw ? "text" : "password"} autoComplete="new-password" required minLength={6}
            data-testid="confirm-password-input"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="bg-slate-950 border-slate-800 text-slate-100 h-11"
          />
        </div>
        {error && (
          <div data-testid="set-password-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <Button type="submit" disabled={loading} data-testid="set-password-submit-button"
          className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isInvite ? "Activate account" : "Update password")}
        </Button>
      </form>
    </AuthShell>
  );
}
